/**
 * useAmbientConfig — Fetches ambient display configuration from backend.
 *
 * DRR-183: Backend-driven ambient display config from st-0-2 Setting.
 * Replaces hardcoded frontend defaults with backend setting values.
 * Falls back to defaults if query fails (offline resilience).
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type { AmbientConfig, AmbientDisplayMode } from '../components/ambient/AmbientDisplay';
import type { AmbientRendererType } from '../components/ambient/AmbientLayoutWrapper';
import type { DashboardPanelConfig } from '../components/ambient/AmbientDashboard';

const AMBIENT_DISPLAY_CONFIG_QUERY = gql`
  query AmbientDisplayConfig {
    ambientDisplayConfig {
      enabled
      defaultRenderer
      defaultDimension
      inactivityTimeoutMinutes
      alertSeverityThreshold
      refreshIntervalSeconds
      fontScale
      criticalOverlayDismissSeconds
      rotationRenderers
      rotationIntervalSeconds
      displayMode
      dashboardPanels {
        slot
        renderer
        dimension
      }
      defaultTraversalLens
    }
  }
`;

const FALLBACK_CONFIG: AmbientConfig = {
  defaultRenderer: 'spatial',
  defaultDimension: 'location',
  inactivityTimeoutMinutes: 15,
  alertSeverityThreshold: 'HIGH',
  refreshIntervalSeconds: 30,
  fontScale: 1.5,
  criticalOverlayDismissSeconds: 30,
  rotationRenderers: ['spatial', 'kanban'],
  rotationIntervalSeconds: 30,
  displayMode: 'dashboard',
  dashboardPanels: [
    { slot: 'left', renderer: 'spatial', dimension: 'location' },
    { slot: 'right', renderer: 'kanban', dimension: 'status' },
  ],
};

interface AmbientDisplayConfigResponse {
  ambientDisplayConfig: {
    enabled: boolean;
    defaultRenderer: string;
    defaultDimension: string;
    inactivityTimeoutMinutes: number;
    alertSeverityThreshold: string;
    refreshIntervalSeconds: number;
    fontScale: number;
    criticalOverlayDismissSeconds: number;
    rotationRenderers: string[];
    rotationIntervalSeconds: number;
    displayMode: string;
    dashboardPanels: Array<{ slot: string; renderer: string; dimension: string }>;
    defaultTraversalLens: string;
  };
}

export interface UseAmbientConfigReturn {
  config: AmbientConfig;
  loading: boolean;
  enabled: boolean;
}

export function useAmbientConfig(): UseAmbientConfigReturn {
  const { data, loading } = useQuery<AmbientDisplayConfigResponse>(
    AMBIENT_DISPLAY_CONFIG_QUERY,
    { fetchPolicy: 'cache-first' },
  );

  const config = useMemo<AmbientConfig>(() => {
    if (!data?.ambientDisplayConfig) return FALLBACK_CONFIG;
    const d = data.ambientDisplayConfig;
    return {
      defaultRenderer: d.defaultRenderer as 'spatial' | 'kanban',
      defaultDimension: d.defaultDimension,
      inactivityTimeoutMinutes: d.inactivityTimeoutMinutes,
      alertSeverityThreshold: d.alertSeverityThreshold,
      refreshIntervalSeconds: d.refreshIntervalSeconds,
      fontScale: d.fontScale,
      criticalOverlayDismissSeconds: d.criticalOverlayDismissSeconds,
      rotationRenderers: d.rotationRenderers as AmbientRendererType[],
      rotationIntervalSeconds: d.rotationIntervalSeconds,
      displayMode: d.displayMode as AmbientDisplayMode,
      dashboardPanels: d.dashboardPanels as DashboardPanelConfig[],
    };
  }, [data]);

  return {
    config,
    loading,
    enabled: data?.ambientDisplayConfig?.enabled ?? false,
  };
}
